import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { getDefaultOnboardingBookingUrl } from "@redotech/redo-onboarding-api/booking-destination";
import { RedoNotificationBanner } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { memo, useContext } from "react";
import { useLocalStorage } from "usehooks-ts";
import { OMS_BOOKING_DESTINATION_ID } from "../../../unified-onboarding/products/oms/catalog";

/**
 * Offers expert help to a team that finished OMS onboarding on its own. It
 * stays up until the merchant opens the scheduler or says not to show it again.
 */
export const OmsExpertHelpBanner = memo(function OmsExpertHelpBanner() {
  const team = useRequiredContext(TeamContext);
  const user = useContext(UserContext);
  const [hidden, setHidden] = useLocalStorage<boolean>(
    `redo.oms-expert-help-banner-hidden.${team._id}.${user?._id ?? ""}`,
    false,
  );

  const eligible =
    !hidden &&
    !!user &&
    team.settings.outboundLabels?.newOmsEnabled === true &&
    !!team.settings.outboundLabels?.omsSelfServeOnboardedAt;

  if (!eligible) {
    return null;
  }
  return <OmsExpertHelpBannerContent onHide={() => setHidden(true)} />;
});

const OmsExpertHelpBannerContent = memo(function OmsExpertHelpBannerContent({
  onHide,
}: {
  onHide: () => void;
}) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const bookingLoad = useLoad(
    async (signal) =>
      rpcClient.getBookingDestination(
        { productId: OMS_BOOKING_DESTINATION_ID },
        { signal },
      ),
    [rpcClient],
  );
  const bookingUrl =
    bookingLoad.value?.bookingUrl ??
    getDefaultOnboardingBookingUrl(OMS_BOOKING_DESTINATION_ID);

  return (
    <RedoNotificationBanner
      data-dd-action-name="oms_expert_help_banner"
      items={[
        {
          id: "oms-expert-help",
          type: "info",
          message:
            "Want a hand getting set up? Book a free white-glove onboarding and a shipping expert will set up your carriers, packaging and automations with you.",
          actions: [
            {
              actionType: "link",
              text: "Schedule onboarding",
              href: bookingUrl,
              target: "_blank",
              internal: false,
              onClick: onHide,
            },
            {
              actionType: "button",
              hierarchy: "tertiary",
              text: "Don't show again",
              onClick: onHide,
            },
          ],
        },
      ]}
    />
  );
});
